@use '../../style/internal/shared_input-select-picker';

/**
 * @prop --form-body-padding: space around content of the form. Defaults to `1rem`.
 * @prop --form-column-gap: defines the vertical space between elements of the form with `grid` and `default` layouts. Defaults to `1rem`.
 * @prop --form-row-gap: defines the horizontal space between elements of the form with `grid` and `default` layouts. Defaults to `1rem`.
 * @prop --form-gap: defines the space between elements of the form with `grid` and `default` layouts. Defaults to `1rem`.
 * @prop --form-background-color-of-even-rows: Background of even rows in the form, when layout type is `row`. Defaults to `transparent`.
 * @prop --form-background-color-of-odd-rows:Background of odd rows in the form, when layout type is `row`. Defaults to `--contrast-200`.
 */

.form-group {
    min-width: 0;
}

.limel-form-array-item--simple {
    display: flex;
    align-items: center;
    padding-bottom: var(--form-row-gap, 1rem);

    *:first-child {
        flex-grow: 1;
    }
}

limel-code-editor {
    margin-bottom: 0.75rem;
}

.limel-form-array-item--object {
    margin-bottom: 0.25rem;
}

.limel-form-layout--default {
    display: grid;
    column-gap: var(--form-column-gap, 1rem);
    row-gap: var(--form-row-gap, 1rem);
    padding: var(--form-body-padding, 1rem);
}

.limel-form-layout--grid {
    --min-height-of-one-row: #{shared_input-select-picker.$height-of-mdc-text-field};
    display: grid;
    column-gap: var(--form-column-gap, 1rem);
    row-gap: var(--form-row-gap, 1rem);
    padding: var(--form-body-padding, 1rem);

    // Using `minmax(0, 1fr)` below, instead of just `1fr` ensures that
    // components that are wider than their column don't make the column grow
    // wider and squash the other columns. /Ads
    grid-template-columns: repeat(var(--number-of-columns), minmax(0, 1fr));

    &.auto-reorder-to-avoid-empty-cells {
        grid-auto-flow: dense;
    }

    &.layout-1-columns {
        .limel-form-layout-colspan--1,
        .limel-form-layout-colspan--2,
        .limel-form-layout-colspan--3,
        .limel-form-layout-colspan--4,
        .limel-form-layout-colspan--5 {
            grid-column: span 1;
        }
    }

    &.layout-2-columns {
        .limel-form-layout-colspan--1 {
            grid-column: span 1;
        }
        .limel-form-layout-colspan--2,
        .limel-form-layout-colspan--3,
        .limel-form-layout-colspan--4,
        .limel-form-layout-colspan--5 {
            grid-column: span 2;
        }
    }

    &.layout-3-columns {
        .limel-form-layout-colspan--1 {
            grid-column: span 1;
        }
        .limel-form-layout-colspan--2 {
            grid-column: span 2;
        }
        .limel-form-layout-colspan--3,
        .limel-form-layout-colspan--4,
        .limel-form-layout-colspan--5 {
            grid-column: span 3;
        }
    }

    &.layout-4-columns {
        .limel-form-layout-colspan--1 {
            grid-column: span 1;
        }
        .limel-form-layout-colspan--2 {
            grid-column: span 2;
        }
        .limel-form-layout-colspan--3 {
            grid-column: span 3;
        }
        .limel-form-layout-colspan--4,
        .limel-form-layout-colspan--5 {
            grid-column: span 4;
        }
    }

    &.layout-5-columns {
        .limel-form-layout-colspan--1 {
            grid-column: span 1;
        }
        .limel-form-layout-colspan--2 {
            grid-column: span 2;
        }
        .limel-form-layout-colspan--3 {
            grid-column: span 3;
        }
        .limel-form-layout-colspan--4 {
            grid-column: span 4;
        }
        .limel-form-layout-colspan--5 {
            grid-column: span 5;
        }
    }

    .limel-form-layout-colspan--all {
        grid-column: 1/-1;
    }
}

.limel-form-layout--grid {
    limel-checkbox,
    limel-switch {
        // This is because we have to align these items to top and
        // make sure they align correctly even in one-columned layouts
        min-height: var(--min-height-of-one-row);
    }
    limel-checkbox {
        //  margin-top: 0.5rem;
        display: block;
    }
    limel-switch {
        margin-left: 0.5rem;
    }
}

.form-error {
    color: var(--limel-theme-error-color);
    font-size: 0.6875rem;
    line-height: 1.5;
    visibility: inherit;
    padding-right: 1rem;
    padding-left: 1rem;
    padding-top: 0.25rem;
}

.button-add-new {
    margin-top: 0.5rem;
    width: 100%;
}

.mdc-typography--headline1 {
    position: relative;
}

.form-group {
    position: relative;

    limel-help {
        position: absolute;
        top: calc(var(--form-row-gap, 1rem) * -0.5);
        left: calc(var(--form-column-gap, 1rem) * -0.5);
    }

    .mdc-typography--headline1,
    .mdc-typography--body1 {
        color: rgb(var(--contrast-1100));
    }

    .mdc-typography--headline1 {
        font-size: 2rem;
        line-height: 2.25rem;
        letter-spacing: -0.01rem;
        font-weight: 400;
        margin-top: 1.5rem;
        margin-bottom: 0.25rem;
    }

    .mdc-typography--body1 {
        margin-top: 0;
        margin-bottom: 0.5rem;
        font-size: var(--limel-theme-default-font-size);
    }

    .form-group {
        .mdc-typography--headline1,
        .mdc-typography--body1 {
            color: rgb(var(--contrast-1200));
        }

        .mdc-typography--headline1 {
            font-size: 1.625rem;
            line-height: 1.25rem;
            font-weight: 300;
            margin-top: 1rem;

            &:before {
                content: '';
                display: block;
                position: absolute;
                top: 0;
                bottom: 0;
                margin: auto;
                left: -0.75rem;

                background-color: var(
                    --lime-primary-color,
                    var(--limel-theme-primary-color)
                );
                width: 0.125rem;
                height: 1.5rem;
                border-radius: 0.125rem;

                opacity: 0.6;
            }
        }

        .form-group {
            .mdc-typography--headline1,
            .mdc-typography--body1 {
                color: rgb(var(--contrast-1300));
            }

            .mdc-typography--headline1 {
                font-size: 1.375rem;
                line-height: 1.5rem;
                margin-top: 0.5rem;

                &:before {
                    display: none;
                }
            }

            .form-group {
                .mdc-typography--headline1,
                .mdc-typography--body1 {
                    color: rgb(var(--contrast-1400));
                }

                .mdc-typography--headline1 {
                    font-size: 1.25rem;
                    line-height: 1.25rem;
                    margin-top: 1rem;

                    &:before {
                        display: none;
                    }
                }
            }
            .form-group {
                .mdc-typography--headline1,
                .mdc-typography--body1 {
                    color: rgb(var(--contrast-1400));
                }

                .mdc-typography--headline1 {
                    font-size: 1.25rem;
                    line-height: 1.25rem;
                    margin-top: 0.25rem;

                    &:before {
                        display: none;
                    }
                }

                .form-group {
                    .mdc-typography--headline1,
                    .mdc-typography--body1 {
                        color: rgb(var(--contrast-1400));
                    }

                    .mdc-typography--headline1 {
                        font-size: 1rem;
                        line-height: 1rem;
                        margin-top: 0.25rem;

                        &:before {
                            display: none;
                        }
                    }
                }
            }
        }
    }
}

@import './row/row.scss';
